'use client';
import { VStack, Text } from '@chakra-ui/react';
import { useAccount, useSignTypedData } from 'wagmi';
import {
  DialogRoot,
  DialogTrigger,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogBackdrop,
  DialogCloseTrigger,
  DialogContent,
} from '@/components/ui/dialog';
import { ModalCloseIcon } from '@/components/ui/svgs/modal-close';
import { ArbitrumIcon } from '@/components/ui/svgs/arbitrum';
import { AmountInputArea } from '@/components/ai-earn/AmountInputArea';
import { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { useHLAccountSummary } from '@/lib/services/hyperliquidInfo.service';
import {
  useWithdrawal,
  requestApproveDetails,
} from '@/lib/services/aiEarn/signature.service';
import { SignTypedDataVariables } from '@wagmi/core/query';
import { useSignWithdraw } from '@/lib/hooks/walletHooks/useWithdraw';

export function WithdrawButton() {
  const { isConnected, address } = useAccount();
  const { signTypedDataAsync } = useSignTypedData();
  const [amount, setAmount] = useState('');
  const { signAndSendWithdrawData, isWithdrawing } = useSignWithdraw();
  const { data: accountSummary, refetch: refetchAccountSummary } =
    useHLAccountSummary({
      user: address ?? '',
    });
  const withdrawButtonState = useMemo(() => {
    if (amount === '') {
      return {
        disabled: true,
        text: 'Withdraw To Arbitrum',
      };
    }
    if (Number(amount) < 2) {
      return {
        disabled: true,
        text: 'Minimum withdrawal：$2',
      };
    }
    return {
      disabled: false,
      text: 'Withdraw To Arbitrum',
    };
  }, [amount]);

  return isConnected ? (
    <DialogRoot placement={'center'}>
      <DialogBackdrop />
      <DialogTrigger asChild>
        <Button
          px={6}
          rounded={8}
          bg="text.t1"
          color="bg.t1"
          fontWeight={500}
          fontSize="sm"
          lineHeight={1.5}
          size="xs"
          _hover={{
            opacity: 0.8,
          }}
        >
          Withdraw
        </Button>
      </DialogTrigger>
      <DialogContent
        maxW="480px"
        width="100%"
        borderRadius="20px"
        gap={6}
        display={'flex'}
        flexDirection={'column'}
        border="1px solid"
        borderColor="text.t4"
        bg="bg.t1"
        pb={5}
      >
        <DialogHeader borderBottom="1px solid" borderColor="text.t4">
          <DialogTitle color="text.t1" pt={5} pb={3} px={5} fontSize="md">
            Withdraw
          </DialogTitle>
          <DialogCloseTrigger asChild right={3} top={3}>
            <ModalCloseIcon />
          </DialogCloseTrigger>
        </DialogHeader>
        <VStack w="full" px={5} gap={10}>
          <VStack w="full" gap={2}>
            <ArbitrumIcon />
            <Text fontWeight={500} fontSize="md" lineHeight={1.5}>
              Withdraw USDC to Arbitrum
            </Text>
            <VStack w="full" gap={'2px'}>
              <Text fontSize={'xs'} lineHeight={1.2} color={'text.t2'}>
                USDC will be sent over the Arbitrum network to your address.
              </Text>
              <Text fontSize={'xs'} lineHeight={1.2} color={'text.t2'}>
                A $1 fee will be deducted from the USDC withdrawal.
              </Text>
            </VStack>
          </VStack>
          <VStack w="full" gap={3} align="start">
            <AmountInputArea
              amount={amount}
              setAmount={setAmount}
              balance={accountSummary?.marginSummary.accountValue ?? '0'}
            />
            {/* {Number(amount) < 5 && amount !== '' && (
              <Text fontSize={'xs'} lineHeight={'16px'} color={'fixed.short'}>
                The minimum deposit amount is 5 USDC.
              </Text>
            )} */}
          </VStack>
          <DialogFooter w="full">
            <VStack gap={3} w="full">
              <Button
                size="xl"
                w="full"
                color="#06080B"
                bg="#AEFEC3"
                rounded={8}
                _hover={{ opacity: 0.8 }}
                loading={isWithdrawing}
                disabled={withdrawButtonState.disabled}
                onClick={async () => {
                  try {
                    await signAndSendWithdrawData({
                      amount,
                    });
                  } catch (e) {
                    console.error(e);
                  }
                }}
              >
                {withdrawButtonState.text}
              </Button>
              <Text color="text.t3" fontSize={'xs'} lineHeight={1.2}>
                Withdrawals should arrive within 5 minutes
              </Text>
            </VStack>
          </DialogFooter>
        </VStack>
      </DialogContent>
    </DialogRoot>
  ) : null;
}
